import { createAsyncThunk } from '@reduxjs/toolkit'
import { goalsAPI } from '../../utils/supabase'

// Generate temporary ID for optimistic updates
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Annual Goals Thunks
export const fetchAnnualGoals = createAsyncThunk(
  'goals/fetchAnnualGoals',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.getAllAnnualGoals(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createAnnualGoal = createAsyncThunk(
  'goals/createAnnualGoal',
  async ({ userId, goalData }, { rejectWithValue, getState }) => {
    const tempId = generateTempId()
    
    try {
      const { data, error } = await goalsAPI.createAnnualGoal(userId, goalData)
      if (error) throw error
      return { 
        data, 
        tempId,
        type: 'create'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        tempId,
        type: 'create'
      })
    }
  }
)

export const updateAnnualGoal = createAsyncThunk(
  'goals/updateAnnualGoal',
  async ({ id, ...updates }, { rejectWithValue, getState }) => {
    const state = getState()
    const originalGoal = state.goals.annualGoals.find(goal => goal.id === id)
    
    try {
      const { data, error } = await goalsAPI.updateAnnualGoal(id, updates)
      if (error) throw error
      return { 
        data, 
        originalGoal,
        type: 'update'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        id,
        originalGoal,
        type: 'update'
      })
    }
  }
)

export const deleteAnnualGoal = createAsyncThunk(
  'goals/deleteAnnualGoal',
  async (id, { rejectWithValue, getState }) => {
    const state = getState()
    const goalToDelete = state.goals.annualGoals.find(goal => goal.id === id)
    
    try {
      const { error } = await goalsAPI.deleteAnnualGoal(id)
      if (error) throw error
      return { 
        id, 
        deletedGoal: goalToDelete,
        type: 'delete'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        id,
        deletedGoal: goalToDelete,
        type: 'delete'
      })
    }
  }
)

// Annual Goals Reducers
export const annualGoalsReducers = {
  // Fetch Annual Goals
  [fetchAnnualGoals.pending]: (state) => {
    state.loading.annual = true
    state.error.annual = null
  },
  [fetchAnnualGoals.fulfilled]: (state, action) => {
    state.loading.annual = false
    state.annualGoals = action.payload
    state.initialized.annual = true
  },
  [fetchAnnualGoals.rejected]: (state, action) => {
    state.loading.annual = false
    state.error.annual = action.payload
  },

  // Create Annual Goal - Optimistic
  [createAnnualGoal.pending]: (state, action) => {
    const { userId, goalData } = action.meta.arg
    const tempId = generateTempId()
    
    const optimisticGoal = {
      ...goalData,
      id: tempId,
      user_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: goalData.status || 'draft',
      isOptimistic: true,
      isLoading: true
    }
    
    state.annualGoals.unshift(optimisticGoal)
    state.error.annual = null
  },
  [createAnnualGoal.fulfilled]: (state, action) => {
    const { data, tempId } = action.payload
    const tempIndex = state.annualGoals.findIndex(goal => goal.isOptimistic && goal.isLoading)
    
    if (tempIndex !== -1) {
      // Replace optimistic goal with real data
      state.annualGoals[tempIndex] = {
        ...data,
        isOptimistic: false,
        isLoading: false
      }
    } else {
      // Fallback: just add the new goal
      state.annualGoals.unshift(data)
    }
  },
  [createAnnualGoal.rejected]: (state, action) => {
    const { tempId } = action.payload
    // Remove the optimistic goal
    state.annualGoals = state.annualGoals.filter(goal => 
      !goal.isOptimistic || !goal.isLoading
    )
    state.error.annual = action.payload.message
  },

  // Update Annual Goal - Optimistic
  [updateAnnualGoal.pending]: (state, action) => {
    const { id, ...updates } = action.meta.arg
    const index = state.annualGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Apply optimistic update
      state.annualGoals[index] = {
        ...state.annualGoals[index],
        ...updates,
        updated_at: new Date().toISOString(),
        isOptimistic: true,
        isLoading: true
      }
    }
    state.error.annual = null
  },
  [updateAnnualGoal.fulfilled]: (state, action) => {
    const { data } = action.payload
    const index = state.annualGoals.findIndex(goal => goal.id === data.id)
    
    if (index !== -1) {
      state.annualGoals[index] = {
        ...data,
        isOptimistic: false,
        isLoading: false
      }
    }
  },
  [updateAnnualGoal.rejected]: (state, action) => {
    const { originalGoal, id } = action.payload
    const index = state.annualGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1 && originalGoal) {
      // Rollback to original state
      state.annualGoals[index] = {
        ...originalGoal,
        isOptimistic: false,
        isLoading: false
      }
    }
    state.error.annual = action.payload.message
  },

  // Delete Annual Goal - Optimistic
  [deleteAnnualGoal.pending]: (state, action) => {
    const id = action.meta.arg
    const index = state.annualGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Mark as deleting instead of removing immediately
      state.annualGoals[index] = {
        ...state.annualGoals[index],
        isOptimistic: true,
        isLoading: true,
        isDeleting: true
      }
    }
    state.error.annual = null
  },
  [deleteAnnualGoal.fulfilled]: (state, action) => {
    const { id } = action.payload
    // Permanently remove the goal
    state.annualGoals = state.annualGoals.filter(goal => goal.id !== id)
  },
  [deleteAnnualGoal.rejected]: (state, action) => {
    const { id, deletedGoal } = action.payload
    const index = state.annualGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Restore the goal (remove deleting state)
      state.annualGoals[index] = {
        ...deletedGoal,
        isOptimistic: false,
        isLoading: false,
        isDeleting: false
      }
    } else if (deletedGoal) {
      // If somehow removed, add it back
      state.annualGoals.push({
        ...deletedGoal,
        isOptimistic: false,
        isLoading: false,
        isDeleting: false
      })
    }
    state.error.annual = action.payload.message
  }
}
